import React, { useState, useEffect } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Heading,
  Text,
  Spinner,
  Flex,
  Divider,
  Link as ChakraLink,
  Button,
  Image,
} from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { getNewsArticle } from "../utils/newsUtils";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";
// If you want tables and other GFM features
import remarkGfm from "remark-gfm";
// If you want to allow HTML in markdown (for captions, etc)
import rehypeRaw from "rehype-raw";

const NewsArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return;
      try {
        setLoading(true);
        const fetchedArticle = await getNewsArticle(slug);
        if (fetchedArticle) {
          setArticle(fetchedArticle);
          setError(null);
        } else {
          setError("Article not found.");
          setArticle(null);
        }
      } catch (err) {
        console.error(`Failed to load article ${slug}:`, err);
        setError("Failed to load the article. Please try again later.");
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  // Custom components for Markdown rendering with improved styling
  const markdownComponents = {
    h1: ({ node, ...props }) => <Heading as="h1" size="xl" my={5} {...props} />,
    h2: ({ node, ...props }) => <Heading as="h2" size="lg" my={4} {...props} />,
    h3: ({ node, ...props }) => <Heading as="h3" size="md" my={3} {...props} />,
    p: ({ node, ...props }) => <Text my={4} {...props} />,
    // Handle links, including nofollow attribute if present in the HTML
    a: ({ node, href, rel, ...props }) => {
      const isExternal =
        href && (href.startsWith("http") || href.startsWith("https"));

      const linkRel = rel || (isExternal ? "noopener" : "");

      return (
        <ChakraLink
          color="blue.500"
          href={href}
          isExternal={isExternal}
          rel={linkRel}
          {...props}
        />
      );
    },
    iframe: ({ node, ...props }) => (
      <Box my={6} display="flex" justifyContent="center">
        <Box as="iframe" {...props} />
      </Box>
    ),
    ul: ({ node, ...props }) => <Box as="ul" pl={5} my={4} {...props} />,
    ol: ({ node, ...props }) => <Box as="ol" pl={5} my={4} {...props} />,
    li: ({ node, ...props }) => <Box as="li" mb={1} {...props} />,
    // Adds caption to img
    img: ({ node, src, alt, title, ...props }) => (
      <Box my={4} textAlign="center">
        <Image
          src={src}
          alt={alt || ""}
          maxW="100%"
          mx="auto"
          borderRadius="md"
          {...props}
        />
        <Text
          fontSize="sm"
          color="gray.600"
          mt={2}
          fontStyle="italic"
          textAlign="center"
        >
          {alt}
          {title && (
            <Text as="span" display="block" mt={1}>
              {title}
            </Text>
          )}
        </Text>
      </Box>
    ),
    blockquote: ({ node, ...props }) => (
      <Box
        as="blockquote"
        borderLeft="4px solid"
        borderColor="gray.300"
        pl={4}
        my={4}
        fontStyle="italic"
        {...props}
      />
    ),
    hr: ({ node, ...props }) => <Divider my={6} {...props} />,
    // Add support for tables if using remarkGfm
    table: ({ node, ...props }) => (
      <Box overflowX="auto" my={4}>
        <Box as="table" width="full" {...props} />
      </Box>
    ),
    thead: ({ node, ...props }) => <Box as="thead" bg="gray.50" {...props} />,
    tbody: ({ node, ...props }) => <Box as="tbody" {...props} />,
    tr: ({ node, ...props }) => <Box as="tr" {...props} />,
    th: ({ node, ...props }) => (
      <Box
        as="th"
        p={2}
        fontWeight="semibold"
        textAlign="left"
        borderBottom="1px"
        borderColor="gray.200"
        {...props}
      />
    ),
    td: ({ node, ...props }) => (
      <Box as="td" p={2} borderBottom="1px" borderColor="gray.200" {...props} />
    ),
    // Support for emphasized text (italic)
    em: ({ node, ...props }) => <Text as="em" fontStyle="italic" {...props} />,
    // Support for strong text (bold)
    strong: ({ node, ...props }) => (
      <Text as="strong" fontWeight="bold" {...props} />
    ),
  };

  // Function to handle HTML content that might include nofollow links
  const processMarkdown = (content) => {
    // If you need to do any pre-processing of the markdown, do it here
    return content;
  };

  return (
    <Flex direction="column" minHeight="100vh">
      {/* SEO Meta Tags */}
      {!loading && !error && article && (
        <Helmet>
          <title>{article.data.title}</title>
          <meta
            name="description"
            content={article.data.metaDescription || ""}
          />
          <meta name="keywords" content={article.data.keywords || ""} />

          {/* OpenGraph Tags for social sharing */}
          <meta property="og:title" content={article.data.title} />
          {article.data.metaDescription && (
            <meta
              property="og:description"
              content={article.data.metaDescription}
            />
          )}
          <meta property="og:type" content="article" />
          <meta property="og:url" content={window.location.href} />
          {article.data.thumbnail && (
            <meta
              property="og:image"
              content={`${window.location.origin}${article.data.thumbnail}`}
            />
          )}

          {/* Twitter Card Tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={article.data.title} />
          {article.data.metaDescription && (
            <meta
              name="twitter:description"
              content={article.data.metaDescription}
            />
          )}
          {article.data.thumbnail && (
            <meta
              name="twitter:image"
              content={`${window.location.origin}${article.data.thumbnail}`}
            />
          )}

          {/* Main keyword as canonical - this helps SEO focus */}
          {article.data.mainKeyword && (
            <meta name="article:tag" content={article.data.mainKeyword} />
          )}

          {/* Structured data for rich results */}
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": "${article.data.title}",
                "author": {
                  "@type": "Person",
                  "name": "${article.data.author || "Admin"}"
                },
                "datePublished": "${article.data.date}",
                "description": "${
                  article.data.metaDescription || article.data.snippet || ""
                }"
              }
            `}
          </script>
        </Helmet>
      )}

      <Box flex="1" p={{ base: 6, md: 16 }} maxW="850px" mx="auto" width="100%">
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="outline"
          mb={6}
          onClick={() => window.history.back()}
        >
          Back to News
        </Button>

        {loading && (
          <Flex justify="center" align="center" height="300px">
            <Spinner size="xl" />
          </Flex>
        )}

        {error && (
          <Text color="red.500" textAlign="center">
            {error}
          </Text>
        )}

        {!loading && !error && article && (
          <Box>
            {article.data.thumbnail && (
              <Image
                src={article.data.thumbnail}
                alt={`${article.data.title} thumbnail`}
                objectFit="cover"
                width="100%"
                maxH="400px"
                mb={6}
                borderRadius="md"
              />
            )}
            <Heading as="h1" size={{ base: "xl", md: "2xl" }} mb={3}>
              {article.data.title}
            </Heading>
            <Text fontSize="md" color="gray.500" mb={5}>
              Published on {new Date(article.data.date).toLocaleDateString()} by{" "}
              {article.data.author || "Admin"}
            </Text>
            <Divider mb={5} />
            <Box className="markdown-content">
              <ReactMarkdown
                components={markdownComponents}
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {article.content}
              </ReactMarkdown>
            </Box>
          </Box>
        )}
      </Box>
      <Footer />
    </Flex>
  );
};

export default NewsArticlePage;
